import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { firstElement } from '@core/util/array';
import { createId, createRandomNumber } from '@core/util/file';
import { SetTitleDetails } from '@core/util/legalDescription';
import {
	buildMatterBridgeMortgage,
	getAmount,
	getDateOrString,
	getInterestRate,
	getInterestRateAbovePrime,
	getInterestRateAsReg,
	getInterestRateType,
	getPrincipleAmount,
	getPriority,
	isMortgageMms,
	mapLLCMortgageeToCloser,
} from '@core/util/llc';
import { dollarsToCents } from '@core/util/money';
import { sanitizePropertyValues } from '@core/util/value';
import { Address } from '@models/address';
import {
	BaseClient,
	BusinessClient,
	ClientType,
	PersonClient,
} from '@models/clients';
import {
	AdvancedAmountLabels,
	LLCTransaction,
	LLCTransactionData,
} from '@models/llc-mortgages';
import {
	Matter,
	MatterClientType,
	MatterPropertyDetails,
	MatterTitle,
} from '@models/matter';
import {
	MatterMortgage,
	MatterMortgageInstructionSource,
	MatterMortgagePriority,
	MatterMortgageType,
	MortgageStep,
	NetAdvancedAmount,
	NetAmountType,
	TransactionType,
} from '@models/mortgage';
import {
	ClientMapping,
	ClientMappingType,
	MortgageTransactionSource,
} from '@models/mortgages/mortgage-base';
import { Select } from '@ngxs/store';
import { MatterMortgagesRepoService } from '@repos/businesses/sales/mortgages.service';
import { ClientsRepoService } from '@repos/clients/clients.service';
import { MatterClientRelationshipRepoService } from '@repos/matter-client-relationship/matterClientRelRepo.service';
import { MatterRepo } from '@repos/matter/matter.repo';
import { SnackbarService } from '@services/ui/snackbar.service';
import { MatterTitlesDispatch } from '@state/matter/titles/titles.dispatch';
import { UserProfileState } from '@state/user-profile';
import firebase from 'firebase/compat/app';
import 'firebase/firestore';
import LogRocket from 'logrocket';
import { take } from 'rxjs/operators';
import { LLCMortgageRepoService } from './llcMortgageRepo';

export interface Update<T> {
	id: string;
	update: Partial<T>;
}

export interface AssignToMatterResponse {
	matterId: string;
	matterMortgageId?: string;
	transactionId: string;
	source: MortgageTransactionSource;
}

export interface MortgageClientMappings {
	closerClientId: string;
	mortgageClientId: number;
	type: MatterClientType;
	clientType: ClientType;
	firstName: string;
	middleNames: string;
	lastName: string;
	name: string;
	action: 'add' | 'existing';
	signingOfficer: boolean;
	occupation: string;
	mortgageId?: string;
}

export interface ClientAddressOveride {
	id: string;
	address: Address;
}

export interface SyncMortageDetails {
	salePrice: number | null;
	closingDate: string | null;
	propertyAddress: Address | null;
	primaryLegalDescriptionId: string | null;
	legalDescription: string | null;
	estateType: string | null;
	clientServiceAddress: ClientAddressOveride[];
}

export interface DeleteLLCMatterMortgage {
	matterId: string;
	relatedMortgageId: string;
}

@Injectable({
	providedIn: 'root',
})
export class LlcRepoService {
	constructor(
		private db: AngularFirestore,
		private matterRepo: MatterRepo,
		private llcMortgageRepo: LLCMortgageRepoService,
		private matterMortgageRepo: MatterMortgagesRepoService,
		private matterClientRelRepo: MatterClientRelationshipRepoService,
		private clientsRepoService: ClientsRepoService,
		private snackbarService: SnackbarService,
		private matterTitleDispatch: MatterTitlesDispatch,
	) {}

	@Select(UserProfileState.getCurrentBusinessId) businessId: string;

	deleteLLCMatterMortgage = async (data: DeleteLLCMatterMortgage) => {
		try {
			const matterMortgages = await this.matterMortgageRepo
				.All({ matterId: data.matterId })
				.pipe(take(1))
				.toPromise();

			const batch = this.db.firestore.batch();
			const llcRef = this.llcMortgageRepo.DocPath(data.relatedMortgageId);
			const LLCMortgageReset: Partial<LLCTransaction> = {
				matterId: null,
				matterMortgageId: null,
				matterName: null,
				matterNumber: null,
				clientMappings: [],
				documentList: null,
			};

			batch.update(this.db.firestore.doc(llcRef), LLCMortgageReset);

			matterMortgages
				.filter(
					(x) =>
						(x as MatterMortgage)?.relatedMortgageId === data.relatedMortgageId,
				)
				.forEach((x) => {
					const ref = this.matterMortgageRepo.DocPath(x.id, {
						matterId: data.matterId,
					});
					batch.delete(this.db.firestore.doc(ref));
				});

			await batch.commit();

			// Sometimes a user accepts an llc mortgage and it creates 2 matter mortgages and the user will want to delete one of the mortgages
			// In this case we only want to unreference
		} catch (err) {
			this.snackbarService.openSnackBar({
				text: 'Unable to unassign LLC mortgage',
				type: 'error',
			});
			LogRocket.error(
				'Unable to delete matter mortgage and unassign from llc mortgage',
				data,
				err,
			);
			throw err;
		}
	};

	applyAmendments = async (
		updatedMatter: Update<Matter>,
		updatedMatterProperty: Update<MatterPropertyDetails>,
		updateMatterMortgages: Update<Partial<MatterMortgage>>[],
		clientUpdates: Update<PersonClient | BusinessClient>[],
		clientAddressUpdates: Update<Address>[],
		clientRemove: string[],
		transactionId: string,
		latestTransactionData: LLCTransactionData,
	): Promise<void> => {
		try {
			LogRocket.log(
				'Attempting to accept amendments',
				transactionId,
				updatedMatter,
				updatedMatterProperty,
				updateMatterMortgages,
				clientUpdates,
				clientAddressUpdates,
				clientRemove,
				transactionId,
				latestTransactionData,
			);

			const batch = this.db.firestore.batch();

			const transactionRef = this.llcMortgageRepo.DocPath(transactionId);
			batch.update(this.db.firestore.doc(transactionRef), {
				data: latestTransactionData,
			});

			const matterRef = this.matterRepo.DocPath(updatedMatter.id, {});

			if (Object.keys(updatedMatter.update).length > 0) {
				batch.set(this.db.firestore.doc(matterRef), updatedMatter.update, {
					merge: true,
				});
			}

			if (Object.keys(updatedMatterProperty.update).length > 0) {
				batch.set(
					this.db.firestore.doc(matterRef),
					{ propertyDetails: { ...updatedMatterProperty.update } },
					{ merge: true },
				);
			}

			updateMatterMortgages.forEach((update) => {
				const ref = this.matterMortgageRepo.DocPath(update.id, {
					matterId: updatedMatter.id,
				});
				batch.set(this.db.firestore.doc(ref), update.update, { merge: true });
			});

			clientUpdates.forEach((update) => {
				const clientsRef = this.clientsRepoService.DocPath(update.id);
				batch.set(this.db.firestore.doc(clientsRef), update.update, {
					merge: true,
				});
			});

			clientAddressUpdates.forEach((update) => {
				const clientsRef = this.clientsRepoService.DocPath(update.id);
				batch.set(
					this.db.firestore.doc(clientsRef),
					{ serviceAddress: update.update },
					{ merge: true },
				);
			});

			clientRemove.forEach((clientId) => {
				if (clientId) {
					const matterClientRef = this.matterClientRelRepo.DocPath(clientId, {
						matterId: updatedMatter.id,
					});
					batch.delete(this.db.firestore.doc(matterClientRef));

					//need to remove mapping from transaction
					const path = `clientMappings.${clientId}`;
					batch.update(this.db.firestore.doc(transactionRef), {
						[path]: firebase.firestore.FieldValue.delete(),
					});
				}
			});

			await batch.commit();
			LogRocket.log('Succesfully accepted amendments');

			return;
		} catch (err) {
			console.log('error accepting ammendments', err);
			LogRocket.error('error accepting amendments', err);
		}
	};

	syncMortgageDataToMatterAndClients = async (
		matterId: string,
		syncMortgageDetails: SyncMortageDetails,
	): Promise<void> => {
		const batch = this.db.firestore.batch();

		const matterRef = this.matterRepo.DocPath(matterId, {});
		const partialMatter: Partial<Matter> = {};
		const partialPropertyDetails: Partial<MatterPropertyDetails> = {};

		if (syncMortgageDetails?.salePrice) {
			partialMatter.purchasePrice = syncMortgageDetails.salePrice;
		}

		if (syncMortgageDetails?.closingDate) {
			partialMatter.closingDate = syncMortgageDetails.closingDate;
		}

		if (syncMortgageDetails?.propertyAddress) {
			partialPropertyDetails.address = syncMortgageDetails.propertyAddress;
		}

		if (syncMortgageDetails?.legalDescription) {
			const title = {
				legalDescription: syncMortgageDetails?.legalDescription,
			} as MatterTitle;
			SetTitleDetails(title);
			if (syncMortgageDetails?.primaryLegalDescriptionId) {
				this.matterTitleDispatch.CreateOrUpdate({
					...title,
					id: syncMortgageDetails?.primaryLegalDescriptionId,
				});
			} else {
				this.matterTitleDispatch.CreateOrUpdate({ ...title, id: createId() });
			}
		}

		if (syncMortgageDetails?.estateType) {
			partialPropertyDetails.interestEstate = syncMortgageDetails.estateType;
		}

		if (Object.keys(partialMatter).length > 0) {
			batch.set(this.db.firestore.doc(matterRef), partialMatter, {
				merge: true,
			});
		}

		if (Object.keys(partialPropertyDetails).length > 0) {
			batch.set(
				this.db.firestore.doc(matterRef),
				{ propertyDetails: { ...partialPropertyDetails } },
				{ merge: true },
			);
		}

		syncMortgageDetails?.clientServiceAddress?.forEach((addMap) => {
			// create batch update
			if (addMap.address) {
				const clientDocRef = this.clientsRepoService.DocPath(addMap.id);
				const client: Partial<BaseClient> = { serviceAddress: addMap.address };
				batch.set(this.db.firestore.doc(clientDocRef), client, { merge: true });
			}
		});

		return batch.commit();
	};

	mapMortgageClientsToMatterClients = async (
		matterId,
		transactionId,
		matterMortgageId: string,
		clientMappings: MortgageClientMappings[],
	): Promise<void> => {
		const mappedClients = clientMappings.filter((x) => !!x.closerClientId);
		//create batch
		const batch = this.db.firestore.batch();

		const matterMortgageDocPath = this.matterMortgageRepo.DocPath(
			matterMortgageId,
			{ matterId },
		);

		const guarantorsClients = mappedClients.filter(
			(x) => x.type === MatterClientType.GUARANTOR,
		);
		const guarantorIds = guarantorsClients?.map((x) => x.closerClientId);
		batch.set(
			this.db.firestore.doc(matterMortgageDocPath),
			{ guarantors: guarantorIds },
			{ merge: true },
		);

		// update client matter mappings
		const transactionDocPath = this.llcMortgageRepo.DocPath(transactionId);

		const mappings = mappedClients.map((mapping) => {
			let type: ClientMappingType;
			if (mapping.signingOfficer) {
				type = 'signingOfficer';
			} else if (mapping.clientType === ClientType.PERSON) {
				type = 'person';
			} else {
				type = 'company';
			}
			const result: ClientMapping = {
				closerClientId: mapping.closerClientId,
				llcId: mapping.mortgageClientId,
				type,
			};

			return result;
		});

		batch.update(this.db.firestore.doc(transactionDocPath), {
			clientMappings: mappings,
		});

		return batch.commit();
	};

	assignMatterToMortgage = async (
		transactionId: string,
		matterId: string,
	): Promise<AssignToMatterResponse> => {
		const batch = this.db.firestore.batch();
		const transactionDocPath = this.llcMortgageRepo.DocPath(transactionId);
		const matterMortgages: MatterMortgage[] =
			await this.buildLLCMortgages(transactionId);
		let primaryMatterMortgageId;
		matterMortgages.forEach((_mortgage) => {
			const matterMortgageId = createId();
			const matterMortgageDocPath = this.matterMortgageRepo.DocPath(
				matterMortgageId,
				{ matterId },
			);
			batch.set(this.db.firestore.doc(matterMortgageDocPath), _mortgage);
			if (_mortgage.transactionType !== TransactionType.BRIDGE) {
				primaryMatterMortgageId = matterMortgageId;
			}
		});
		batch.update(this.db.firestore.doc(transactionDocPath), {
			matterId,
			matterMortgageId: primaryMatterMortgageId,
		});
		await batch.commit();

		return {
			matterId,
			matterMortgageId: primaryMatterMortgageId,
			transactionId: transactionId,
			source: MortgageTransactionSource.LLC,
		};
	};

	mapLLCToCloserMortgageData = (
		transactionId: string,
		transaction: LLCTransaction,
		matterMortgageType: TransactionType,
	): Partial<MatterMortgage> => {
		if (matterMortgageType === TransactionType.BRIDGE) {
			return buildMatterBridgeMortgage(transactionId, transaction);
		}

		return this.buildMatterMortgage(transactionId, transaction);
	};

	buildLLCMortgages = async (
		transactionId: string,
	): Promise<MatterMortgage[]> => {
		const result: MatterMortgage[] = [];

		const transactionDoc = await this.llcMortgageRepo
			.Get(transactionId)
			.toPromise();
		const transaction = transactionDoc.data() as LLCTransaction;

		const sourceId = createRandomNumber();

		result.push({
			...this.buildMatterMortgage(transactionId, transaction),
			numericId: sourceId,
			guarantors: [],
		} as MatterMortgage);
		result[0].step = MortgageStep.INTAKE;

		if (transaction?.data?.bridgeLoan) {
			result.push({
				...buildMatterBridgeMortgage(transactionId, transaction),
				numericId: sourceId,
				guarantors: [],
			} as MatterMortgage);
		}

		return result;
	};

	buildMatterMortgage = (
		transactionId: string,
		transaction: LLCTransaction,
	): Partial<MatterMortgage> => {
		let priority: MatterMortgagePriority;

		if (transaction?.data?.properties?.length > 0) {
			priority = getPriority(transaction.data.properties[0].mortgagePriority);
		}
		const interestRateType = getInterestRateType(
			transaction.data?.mortgage?.interestRate,
		);

		const property =
			transaction?.data?.properties.length > 0
				? transaction?.data?.properties[0]
				: null;
		const mortgagePayment =
			transaction?.data?.mortgage.mortgagePayments?.length > 0
				? transaction?.data?.mortgage.mortgagePayments[0]
				: null;

		const mortgage = transaction?.data?.mortgage;
		const isMMS = isMortgageMms(transaction?.data);
		const regularPaymentsAsReg = firstElement(mortgage?.mortgagePayments)
			?.paymentAmountText?.textEn;

		const _mortgage: Partial<MatterMortgage> = {
			referenceNumber: transaction?.data?.lenderReferenceNumber,
			dealId: transaction?.data?.sourceFctUrn,
			relatedMortgageId: transactionId,
			type: MatterMortgageType.LLC,
			priority,
			transactionType: TransactionType.ARRANGED,
			amount: getPrincipleAmount(mortgage),
			loanAmount:
				dollarsToCents(mortgage?.totalLoanAmount) ??
				dollarsToCents(mortgage?.mortgageAmountAdvanced),
			lenderCollectsPropertyTaxes: property?.lenderCollectsPropertyTaxes,
			firstPaymentDate: getDateOrString(
				mortgagePayment?.firstPaymentDate,
				mortgagePayment?.firstPaymentDateText,
			),
			interestAdjustmentDate: getDateOrString(
				mortgage?.interestAdjustmentDate,
				mortgage?.interestAdjustmentDateText,
			),
			maturityDate: getDateOrString(
				mortgage?.maturityDate,
				mortgage?.maturityDateText,
			),
			netAdvancedOverride: dollarsToCents(
				transaction?.data?.mortgageLoanFees?.netMortgageAdvance,
			),
			interestRateType,
			interestRate: getInterestRate(mortgage?.interestRate),
			interestAbovePrime: getInterestRateAbovePrime(
				mortgage?.interestRate?.incrementAboveBelowPrime,
			),
			interestCalculated:
				mortgage?.calculationPeriod?.calculationPeriodText?.textEn,
			interestRateAsReg: getInterestRateAsReg(
				mortgage?.registrationInstructions,
			),
			interestRateTypeAsReg:
				mortgage?.registrationInstructions?.interestRateType,
			paymentDue: mortgagePayment?.paymentDateAndPeriodText?.textEn,
			paymentFrequency: mortgage?.registrationPaymentFreq,
			principalAndInterest: getAmount(
				firstElement(mortgage?.mortgagePayments)?.paymentAmount,
				firstElement(mortgage?.mortgagePayments)?.paymentAmountText?.textEn,
			),
			taxes:
				dollarsToCents(
					firstElement(mortgage?.mortgagePayments)?.propertyTaxAmount,
				) ?? null,
			other:
				dollarsToCents(
					firstElement(mortgage?.mortgagePayments)?.insuranceAmount,
				) ?? null,
			mms: isMMS,
			netAdvanced: dollarsToCents(
				transaction?.data?.mortgageLoanFees?.netMortgageAdvance,
			),
			regularPayments:
				dollarsToCents(firstElement(mortgage?.mortgagePayments)?.total) ?? null,
			regularPaymentsAsReg:
				(typeof regularPaymentsAsReg === 'number'
					? (regularPaymentsAsReg as number).toString()
					: regularPaymentsAsReg) ?? null,
			term: mortgage?.mortgageTerm?.toString() ?? null,
			netAdvancedAmounts: this.buildMortgageFees(transaction),
			source: MatterMortgageInstructionSource.LLC,
			assignmentOfRents: isMMS ? false : mortgage?.assignmentOfRents,
			standardChargeTerm: mortgage?.standardChargeTermText?.textEn,
			...mapLLCMortgageeToCloser(transaction),
		};

		return sanitizePropertyValues(_mortgage);
	};

	buildMortgageFees(llcTransaction: LLCTransaction): NetAdvancedAmount[] {
		const advances: NetAdvancedAmount[] = [];

		if (llcTransaction?.data?.mortgageLoanFees?.appraisalFee) {
			advances.push({
				id: createId(),
				description: AdvancedAmountLabels.APPRAISAL_FEE,
				amount: dollarsToCents(
					llcTransaction?.data?.mortgageLoanFees?.appraisalFee,
				),
				type: NetAmountType.APPRAISAL_FEE,
				calculated: true,
			});
		}

		if (llcTransaction?.data?.mortgageLoanFees?.holdBackFee) {
			advances.push({
				id: createId(),
				description: AdvancedAmountLabels.HOLD_BACK_FEE,
				amount: dollarsToCents(
					llcTransaction?.data?.mortgageLoanFees?.holdBackFee,
				),
				type: NetAmountType.HOLDBACK_FEE,
				calculated: true,
			});
		}

		if (llcTransaction?.data?.mortgageLoanFees?.insuranceApplicationFee) {
			advances.push({
				id: createId(),
				description: AdvancedAmountLabels.INSURANCE_APP_FEE,
				amount: dollarsToCents(
					llcTransaction?.data?.mortgageLoanFees?.insuranceApplicationFee,
				),
				type: NetAmountType.INSURANCE_APPLICATION_FEE,
				calculated: true,
			});
		}

		if (llcTransaction?.data?.mortgageLoanFees?.insurancePremium) {
			advances.push({
				id: createId(),
				description: AdvancedAmountLabels.INSURANCE_PREMIUM,
				amount: dollarsToCents(
					llcTransaction?.data?.mortgageLoanFees?.insurancePremium,
				),
				type: NetAmountType.INSURANCE_PREMIUM,
				calculated: true,
			});
		}

		if (llcTransaction?.data?.mortgageLoanFees?.lenderFee) {
			advances.push({
				id: createId(),
				description: AdvancedAmountLabels.LENDER_FEE,
				amount: dollarsToCents(
					llcTransaction?.data?.mortgageLoanFees?.lenderFee,
				),
				type: NetAmountType.LENDER_FEE,
				calculated: true,
			});
		}

		if (llcTransaction?.data?.mortgageLoanFees?.miscellaneousFee) {
			advances.push({
				id: createId(),
				description: AdvancedAmountLabels.MISC_FEE,
				amount: dollarsToCents(
					llcTransaction?.data?.mortgageLoanFees?.miscellaneousFee,
				),
				type: NetAmountType.MISC_FEE,
				calculated: true,
			});
		}

		if (llcTransaction?.data?.mortgageLoanFees?.wireFee) {
			advances.push({
				id: createId(),
				description: AdvancedAmountLabels.WIRE_FEE,
				amount: dollarsToCents(llcTransaction?.data?.mortgageLoanFees?.wireFee),
				type: NetAmountType.WIRE_FEE,
				calculated: true,
			});
		}

		if (llcTransaction?.data?.mortgageLoanFees?.taxOnInsurancePremium) {
			advances.push({
				id: createId(),
				description: AdvancedAmountLabels.TAX_INSURANCE_PREMIUM,
				amount: dollarsToCents(
					llcTransaction?.data?.mortgageLoanFees?.taxOnInsurancePremium,
				),
				type: NetAmountType.TAX_INS_PREM,
				calculated: true,
			});
		}

		if (llcTransaction?.data?.mortgageLoanFees?.buyDownAtCommitmentFee) {
			advances.push({
				id: createId(),
				description: AdvancedAmountLabels.BUY_DOWN_COMMITMENT_FEE,
				amount: dollarsToCents(
					llcTransaction?.data?.mortgageLoanFees?.buyDownAtCommitmentFee,
				),
				type: NetAmountType.OTHER,
				calculated: true,
			});
		}

		if (llcTransaction?.data?.mortgageLoanFees?.lineOfCredit) {
			advances.push({
				id: createId(),
				description: AdvancedAmountLabels.LINE_OF_CREDIT,
				amount: dollarsToCents(
					llcTransaction?.data?.mortgageLoanFees?.lineOfCredit,
				),
				type: NetAmountType.OTHER,
				calculated: true,
			});
		}

		if (llcTransaction?.data?.mortgageLoanFees?.taxHoldback) {
			advances.push({
				id: createId(),
				description: AdvancedAmountLabels.TAX_HOLDBACK,
				amount: dollarsToCents(
					llcTransaction?.data?.mortgageLoanFees?.taxHoldback,
				),
				type: NetAmountType.OTHER,
				calculated: true,
			});
		}

		if (llcTransaction?.data?.mortgageLoanFees?.internalMortgagePayout) {
			advances.push({
				id: createId(),
				description: AdvancedAmountLabels.INTERNAL_MORTGAGE_PAYOUT,
				amount: dollarsToCents(
					llcTransaction?.data?.mortgageLoanFees?.internalMortgagePayout,
				),
				type: NetAmountType.OTHER,
				calculated: true,
			});
		}

		if (llcTransaction?.data?.mortgageLoanFees?.rebate) {
			advances.push({
				id: createId(),
				description: AdvancedAmountLabels.REBATE,
				amount: dollarsToCents(llcTransaction?.data?.mortgageLoanFees?.rebate),
				type: NetAmountType.OTHER_CREDIT,
				calculated: true,
			});
		}

		if (llcTransaction?.data?.mortgageLoanFees?.fctFee) {
			advances.push({
				id: createId(),
				description: AdvancedAmountLabels.FCT_FEE,
				amount: dollarsToCents(llcTransaction?.data?.mortgageLoanFees?.fctFee),
				type: NetAmountType.LENDER_FEE,
				calculated: true,
			});
		}

		return advances;
	}

	async addAmmendmentHistory(
		transactionId: string,
		ammendments: unknown,
	): Promise<void> {
		const date = new Date().getTime().toString();

		return this.llcMortgageRepo.Update(transactionId, {
			[`ammendmentHistory.${date}`]: ammendments,
		});
	}
}
